// Backend proxy server configuration - use relative paths for production deployment
const BACKEND_API_URL = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api';

class MaestroApiService {
  constructor() {
    this.backendUrl = BACKEND_API_URL;
    // Service-level deduplication - prevent any duplicate calls
    this.activeRequests = new Map(); // url -> promise
    this.scrapedResults = new Map(); // url -> result cache
    
    // Retry configuration for Maestro API errors
    this.MAX_RETRIES = 3; // Retry up to 3 times (4 total attempts)
    this.INITIAL_BACKOFF_MS = 2000; // Start with 2 second delay
    this.MAX_BACKOFF_MS = 16000; // Max 16 seconds between retries
  }

  /**
   * Scrape LinkedIn profile data using Maestro API (via Apify)
   * @param {string} linkedinUrl - The LinkedIn profile URL to scrape
   * @returns {Promise<Object>} Formatted profile data
   */
  async scrapeLinkedInProfile(linkedinUrl) {
    console.log('🚀 [Maestro] Starting LinkedIn profile scraping for:', linkedinUrl);
    
    // Check if we already have a result cached
    if (this.scrapedResults.has(linkedinUrl)) {
      console.log('✅ [Maestro] Using cached result from service for:', linkedinUrl);
      return this.scrapedResults.get(linkedinUrl);
    }
    
    // Check if a request is already active for this URL
    if (this.activeRequests.has(linkedinUrl)) {
      console.log('⏳ [Maestro] Request already active for URL, waiting for existing request:', linkedinUrl);
      return this.activeRequests.get(linkedinUrl);
    }
    
    // Create new scraping promise
    const scrapingPromise = this.performScraping(linkedinUrl);
    
    // Store active request to prevent duplicates
    this.activeRequests.set(linkedinUrl, scrapingPromise);
    
    try {
      const result = await scrapingPromise;
      // Cache the result
      this.scrapedResults.set(linkedinUrl, result);
      return result;
    } finally {
      // Clean up active request tracking
      this.activeRequests.delete(linkedinUrl);
    }
  }

  /**
   * Perform the actual scraping operation with retry logic
   * @param {string} linkedinUrl 
   * @returns {Promise<Object>}
   */
  async performScraping(linkedinUrl) {
    let lastError = null;
    
    // Retry loop for handling transient errors and polling timeouts
    for (let attempt = 0; attempt <= this.MAX_RETRIES; attempt++) {
      try {
        // Log retry attempt
        if (attempt > 0) {
          console.log(`🔄 [Maestro] Scraping restart attempt ${attempt}/${this.MAX_RETRIES} for:`, linkedinUrl);
        }
        
        // Trigger scraping job
        const jobResponse = await this.triggerScraping(linkedinUrl);
        
        if (!jobResponse.success) {
          throw new Error('Failed to trigger scraping job');
        }

        let scrapedData;
        let mongoRecordId = null;
        
        // Poll for results if we have a job ID
        if (jobResponse.jobId) {
          console.log('🔄 [Maestro] Job ID received, starting polling for results...');
          const pollResult = await this.pollForResults(jobResponse.jobId);
          
          if (pollResult && typeof pollResult === 'object' && pollResult.profileData) {
            scrapedData = pollResult.profileData;
            mongoRecordId = pollResult.mongoRecordId;
            console.log('📝 [MongoDB] Record ID:', mongoRecordId || 'Will be created on save');
          } else {
            scrapedData = pollResult; // Fallback for old format
          }
        } else {
          console.log('⚠️ [Maestro] No job ID received, using mock data from backend...');
          scrapedData = jobResponse.mockData || this.getMockData();
        }
        
        // Format and return the data with MongoDB record ID
        const formattedData = this.formatProfileData(scrapedData);
        formattedData._mongoRecordId = mongoRecordId; // Attach record ID to data
        console.log('✅ [Maestro] Profile scraping completed for:', linkedinUrl);
        
        return formattedData;
        
      } catch (error) {
        lastError = error;
        const errorMsg = error.message || String(error);
        
        // Check if this is a polling timeout error
        const isPollingTimeout = errorMsg.includes('Polling timeout') || errorMsg.includes('Polling timed out');
        
        // Check if error is retryable (500, 502, 503)
        const isRetryableError = this.isRetryableError(errorMsg);
        
        // For polling timeout, restart entire scraping process
        if (isPollingTimeout && attempt < this.MAX_RETRIES) {
          const backoffMs = Math.min(
            this.INITIAL_BACKOFF_MS * Math.pow(2, attempt),
            this.MAX_BACKOFF_MS
          );
          
          console.warn(
            `⏰ [Maestro] Polling timeout on attempt ${attempt + 1}/${this.MAX_RETRIES + 1}. ` +
            `Restarting entire scraping process in ${backoffMs}ms...`
          );
          
          // Wait before restarting
          await this.delay(backoffMs);
          continue; // Restart entire scraping process (new job trigger)
        }
        
        // For other retryable errors (500, 502, 503)
        if (isRetryableError && attempt < this.MAX_RETRIES) {
          // Calculate exponential backoff delay
          const backoffMs = Math.min(
            this.INITIAL_BACKOFF_MS * Math.pow(2, attempt),
            this.MAX_BACKOFF_MS
          );
          
          console.warn(
            `⚠️ [Maestro] Retryable error (${this.getErrorCode(errorMsg)}) on attempt ${attempt + 1}/${this.MAX_RETRIES + 1}. ` +
            `Restarting scraping in ${backoffMs}ms: ${errorMsg}`
          );
          
          // Wait before retrying
          await this.delay(backoffMs);
          continue; // Retry the request
        }
        
        // If non-retryable or max retries reached, break the loop
        if (!isRetryableError && !isPollingTimeout) {
          console.error('❌ [Maestro] Non-retryable error encountered:', errorMsg);
        } else {
          console.error(`❌ [Maestro] Max retries (${this.MAX_RETRIES}) reached for:`, linkedinUrl);
        }
        break;
      }
    }
    
    // All retries exhausted, fall back to mock data
    console.warn('❌ [Maestro] API scraping failed after all retries, using fallback mock data:', lastError?.message);
    
    // Return formatted mock data as fallback
    const mockData = this.getMockData();
    const formattedData = this.formatProfileData(mockData);
    
    return formattedData;
  }

  /**
   * Check if an error is retryable based on error message
   * @param {string} errorMsg 
   * @returns {boolean}
   */
  isRetryableError(errorMsg) {
    // Check for 500, 502, 503 status codes
    return /\b(500|502|503)\b/.test(errorMsg) ||
           errorMsg.includes('Bad Gateway') ||
           errorMsg.includes('Service Unavailable') ||
           errorMsg.includes('Internal Server Error') ||
           errorMsg.includes('Temporary');
  }

  /**
   * Extract error code from error message
   * @param {string} errorMsg 
   * @returns {string}
   */
  getErrorCode(errorMsg) {
    const match = errorMsg.match(/\b(500|502|503)\b/);
    return match ? match[1] : 'Unknown';
  }

  /**
   * Trigger the scraping job with Maestro API via backend proxy
   * @param {string} linkedinUrl 
   * @returns {Promise<Object>}
   */
  async triggerScraping(linkedinUrl) {
    console.log('📡 [Maestro] Sending scrape request to backend...');
    
    const response = await fetch(`${this.backendUrl}/scrape-linkedin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ url: linkedinUrl })
    });

    if (!response.ok) {
      throw new Error(`Backend API call failed: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || data.message || 'Backend API call failed');
    }

    return data;
  }

  /**
   * Poll for scraping results from Maestro API
   * @param {string} jobId 
   * @returns {Promise<Object>}
   */
  async pollForResults(jobId, maxAttempts = 120, interval = 3000) {
    console.log(`🔄 [Maestro] Polling for job results: ${jobId}`);
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        // Reduce logging frequency for performance (only log every 3rd attempt after first 3)
        if (attempt % 3 === 0 || attempt < 3) {
          console.log(`🔍 [Maestro] Poll attempt ${attempt + 1}/${maxAttempts}`);
        }
        
        const response = await fetch(`${this.backendUrl}/scrape-status/${jobId}`);
        
        if (response.ok) {
          const data = await response.json();
          
          // Handle SUCCEEDED status (Maestro API status)
          if (data.status === 'SUCCEEDED' && data.data) {
            console.log('✅ [Maestro] Scraping job succeeded!');
            const profileData = Array.isArray(data.data) ? data.data[0] : data.data;
            // Return both profile data and MongoDB record info
            return {
              profileData,
              mongoRecordId: data.mongodb?.recordId || null
            };
          }
          
          // Handle completed status (for compatibility)
          if (data.status === 'completed' && data.data) {
            console.log('✅ [Maestro] Scraping job completed!');
            const profileData = Array.isArray(data.data) ? data.data[0] : data.data;
            return {
              profileData,
              mongoRecordId: data.mongodb?.recordId || null
            };
          }
          
          // Handle failed statuses
          if (data.status === 'FAILED' || data.status === 'ABORTED' || data.status === 'TIMED-OUT' || data.status === 'failed') {
            throw new Error(`Scraping job ${data.status.toLowerCase()} on server`);
          }
          
          // Reduce status logging frequency for performance
          if (attempt % 5 === 0) {
            console.log(`⏳ [Maestro] Job status: ${data.status || 'RUNNING'} - waiting ${interval/1000}s...`);
          }
        } else {
          console.warn(`⚠️ [Maestro] Status check failed: ${response.status}`);
        }

        // Wait before next attempt
        await this.delay(interval);
        
      } catch (error) {
        console.warn(`❌ [Maestro] Poll attempt ${attempt + 1} failed:`, error.message);
        
        if (attempt === maxAttempts - 1) {
          // Throw timeout error instead of returning mock data
          throw new Error(`Polling timeout: Max poll attempts (${maxAttempts}) reached for job ${jobId}`);
        }
        
        await this.delay(interval);
      }
    }

    // Throw timeout error instead of returning mock data
    throw new Error(`Polling timed out after ${maxAttempts} attempts for job ${jobId}`);
  }

  /**
   * Format the scraped data from Maestro API
   * Handles the nested basic_info structure and normalizes it
   * @param {Object} rawData 
   * @returns {Object}
   */
  formatProfileData(rawData) {
    const currentDate = new Date().toISOString();
    
    // Extract basic_info if present (Maestro API structure)
    const basicInfo = rawData.basic_info || {};
    const location = basicInfo.location || {};
    
    return {
      // Metadata
      scrapped_date_time: currentDate,
      timestamp: rawData.timestamp || currentDate.split('T')[0],
      
      // Basic profile info - normalized from basic_info
      avatar: rawData.avatar || basicInfo.profile_picture_url || rawData.profile_picture_url || '',
      name: rawData.name || basicInfo.fullname || rawData.fullname || '',
      position: rawData.position || basicInfo.headline || rawData.headline || '',
      headline: rawData.headline || basicInfo.headline || '',
      url: rawData.url || rawData.input_url || rawData.linkedin_url || '',
      about: rawData.about || basicInfo.about || '',
      
      // Banner image - critical fix from documentation
      banner_image: rawData.banner_image || basicInfo.background_picture_url || rawData.background_picture_url || rawData.background_image || '',
      background_picture_url: basicInfo.background_picture_url || rawData.background_picture_url || rawData.banner_image || '',
      
      // Location info
      city: rawData.city || location.city || location.full || '',
      country_code: rawData.country_code || location.country_code || '',
      location: location.full || rawData.location || '',
      
      // Social metrics - from basic_info
      followers: rawData.followers || basicInfo.follower_count || 0,
      connections: rawData.connections || basicInfo.connection_count || 0,
      
      // Premium/Creator flags
      is_creator: rawData.is_creator ?? basicInfo.is_creator ?? false,
      is_premium: rawData.is_premium ?? basicInfo.is_premium ?? false,
      
      // Public identifier
      public_identifier: rawData.public_identifier || basicInfo.public_identifier || '',
      linkedin_num_id: rawData.linkedin_num_id || '',
      
      // Current company details
      current_company: {
        name: rawData.current_company?.name || rawData.current_company_name || '',
        title: rawData.current_company?.title || rawData.position || basicInfo.headline || '',
        location: rawData.current_company?.location || rawData.city || location.full || '',
        link: rawData.current_company?.url || '',
        company_id: rawData.current_company?.company_id || rawData.current_company_company_id || '',
        logo_url: rawData.current_company?.company_logo_url || ''
      },
      
      // Experience data
      experience: this.formatExperience(rawData.experience || []),
      
      // Recommendations - handle Maestro's nested structure
      recommendations: this.extractRecommendations(rawData),
      recommendations_received: rawData.recommendations_received || rawData.recommendations?.received_recommendations || [],
      recommendations_count: this.getRecommendationsCount(rawData),
      
      // Education details
      education: this.formatEducation(rawData.education || []),
      educations_details: this.getEducationSummary(rawData.education || []),
      
      // Certifications
      certifications: this.formatCertifications(rawData.certifications || []),
      
      // Additional sections
      // Normalize honors/awards from multiple possible Maestro keys
      // Prefer explicit honors_and_awards, then honors, then awards
      honors_and_awards: rawData.honors_and_awards || rawData.honors || rawData.awards || [],
      projects: rawData.projects || [],
      patents: rawData.patents || [],
      publications: rawData.publications || [],
      
      // Volunteering - handle hyphenated key from Maestro
      volunteering: rawData.volunteering || rawData['volunteering-experiences'] || [],
      
      // Featured content
      featured: rawData.featured || [],
      
      // Languages
      languages: rawData.languages || [],
      
      // Skills
      skills: rawData.skills || [],
      
      // Preserve original basic_info for backward compatibility
      basic_info: rawData.basic_info || null
    };
  }

  /**
   * Extract recommendations from various possible structures
   * @param {Object} rawData 
   * @returns {Array}
   */
  extractRecommendations(rawData) {
    // Check for nested structure (Maestro API)
    if (rawData.recommendations?.received_recommendations) {
      return rawData.recommendations.received_recommendations;
    }
    
    // Check for direct structure
    if (rawData.recommendations_received) {
      return rawData.recommendations_received;
    }
    
    // Fallback to simple array
    if (Array.isArray(rawData.recommendations)) {
      return rawData.recommendations;
    }
    
    return [];
  }

  /**
   * Get recommendations count from various structures
   * @param {Object} rawData 
   * @returns {number}
   */
  getRecommendationsCount(rawData) {
    if (rawData.recommendations_count) {
      return rawData.recommendations_count;
    }
    
    const recs = this.extractRecommendations(rawData);
    return recs.length || 0;
  }

  /**
   * Format experience data
   * @param {Array} experience 
   * @returns {Array}
   */
  formatExperience(experience) {
    if (!Array.isArray(experience)) return [];
    
    return experience.map(exp => ({
      company: exp.company || '',
      company_id: exp.company_id || '',
      company_logo_url: exp.company_logo_url || '',
      title: exp.title || '',
      start_date: exp.start_date || '',
      end_date: exp.end_date || '',
      location: exp.location || '',
      description: exp.description || '',
      description_html: exp.description_html || '',
      url: exp.url || ''
    }));
  }

  /**
   * Format education data
   * @param {Array} education 
   * @returns {Array}
   */
  formatEducation(education) {
    if (!Array.isArray(education)) return [];
    
    return education.map(edu => ({
      title: edu.title || edu.school || '',
      degree: edu.degree || edu.field_of_study || '',
      start_year: edu.start_year || '',
      end_year: edu.end_year || '',
      description: edu.description || '',
      description_html: edu.description_html || '',
      institute_logo_url: edu.school_logo_url || edu.institute_logo_url || edu.school_logo || '',
      url: edu.url || ''
    }));
  }

  /**
   * Format certifications data
   * @param {Array} certifications 
   * @returns {Array}
   */
  formatCertifications(certifications) {
    if (!Array.isArray(certifications)) return [];
    
    return certifications.map(cert => ({
      title: cert.title || cert.name || '',
      subtitle: cert.subtitle || cert.issuing_organization || '',
      meta: cert.meta || cert.issue_date || '',
      credential_id: cert.credential_id || '',
      credential_url: cert.credential_url || cert.url || ''
    }));
  }

  /**
   * Get education summary
   * @param {Array} education 
   * @returns {string}
   */
  getEducationSummary(education) {
    if (!education || education.length === 0) return '';
    
    const primary = education[0];
    return `${primary.degree || ''} ${primary.field_of_study || ''} from ${primary.school || primary.title || ''}`.trim();
  }

  /**
   * Utility function to delay execution
   * @param {number} ms 
   * @returns {Promise}
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get mock data for development/fallback
   * @returns {Object}
   */
  getMockData() {
    return {
      basic_info: {
        fullname: "Sample Executive",
        headline: "Chief Technology Officer at TechCorp",
        profile_picture_url: "https://media.licdn.com/dms/image/sample/profile-displayphoto-shrink_200_200/0/sample?e=2147483647&v=beta&t=sample",
        background_picture_url: "https://media.licdn.com/dms/image/sample/profile-displaybackgroundimage-shrink_200_800/0/sample?e=2147483647&v=beta&t=sample",
        public_identifier: "sample-executive",
        location: {
          city: "San Francisco",
          country_code: "US",
          full: "San Francisco, CA, United States"
        },
        follower_count: 5000,
        connection_count: 500,
        is_creator: false,
        is_premium: true
      },
      timestamp: "2025-08-22",
      linkedin_num_id: "sample123",
      url: "https://linkedin.com/in/sample-profile",
      about: "Experienced executive with 15+ years in technology leadership, driving digital transformation and scaling high-performing teams.",
      experience: [
        {
          company: "TechCorp",
          company_id: "techcorp",
          company_logo_url: "https://media.licdn.com/dms/image/company-logo_100_100/0/sample",
          description: "Leading technology strategy and digital transformation initiatives for a Fortune 500 company.",
          description_html: "Leading technology strategy and digital transformation initiatives for a Fortune 500 company.",
          end_date: "Present",
          start_date: "Jan 2020",
          title: "Chief Technology Officer",
          location: "San Francisco, CA",
          url: "https://linkedin.com/company/techcorp"
        }
      ],
      education: [
        {
          degree: "MBA",
          field_of_study: "Business Administration",
          start_year: "2005",
          end_year: "2007",
          title: "Stanford University",
          institute_logo_url: "https://media.licdn.com/dms/image/school-logo_100_100/0/sample",
          url: "https://linkedin.com/school/stanford-university"
        }
      ],
      certifications: [
        {
          title: "Certified Executive Leadership",
          subtitle: "Executive Leadership Institute",
          meta: "Issued Dec 2023",
          credential_id: "ELI123456",
          credential_url: "https://sample-cert-url.com"
        }
      ],
      recommendations: {
        received_recommendations: [
          {
            text: "Outstanding technology leader with exceptional strategic vision and execution capabilities.",
            recommender: "John Doe"
          }
        ]
      },
      honors_and_awards: [
        {
          title: "Technology Leader of the Year",
          issuer: "Tech Industry Awards",
          date: "2023"
        }
      ],
      projects: [],
      patents: [],
      publications: [],
      languages: [],
      skills: [],
      volunteering: []
    };
  }
}

export default new MaestroApiService();
